import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

/** One of the organization's own background tracks. */
export interface MusicUpload {
  id: string;
  name: string;
  durationSec: number | null;
  /** Where the best part starts (seconds); renders start the music here. */
  startSec: number | null;
  /** Stored file URL — what a draft saves as `musicUrl`. */
  blobUrl: string;
  /** Presigned link for listening in the browser. */
  playUrl: string;
  createdAt: string;
}

export const listMusicUploadsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.videos.musicUploads(),
    queryFn: () =>
      apiClient.get<{ uploads: MusicUpload[] }>('videos/music-uploads'),
    // Always refetch on open: an upload made in another tab (or on the Music
    // page) must show up in the Edit music pickers straight away, and play
    // links are presigned for an hour anyway.
    staleTime: 0,
    refetchOnMount: 'always',
  });

export const useListMusicUploads = (enabled = true) => {
  const query = useQuery({ ...listMusicUploadsQueryOptions(), enabled });
  return {
    uploads: query.data?.uploads ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useCreateMusicUpload = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      name: string;
      blobUrl: string;
      durationSec?: number;
      startSec?: number;
    }) => apiClient.post<MusicUpload>('videos/music-uploads', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.videos.musicUploads(),
      });
    },
  });
  return {
    createMusicUploadAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
  };
};

export const useDeleteMusicUpload = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ id: string }>(`videos/music-uploads/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.videos.musicUploads(),
      });
    },
  });
  return {
    deleteMusicUpload: mutation.mutate,
    isDeleting: mutation.isPending,
    deletingId: mutation.isPending ? mutation.variables : undefined,
  };
};

/** An upload as a pickable track for the Edit music dialog. */
export const musicUploadAsTrack = (upload: MusicUpload) => ({
  id: `upload-${upload.id}`,
  name: upload.name,
  durationSec: upload.durationSec ?? 0,
  url: upload.playUrl,
  saveUrl: upload.blobUrl,
  ...(upload.startSec !== null ? { startSec: upload.startSec } : {}),
});
